import { useCallback, useEffect, useRef, useState } from 'react';

import { ParsedAddress, buildAddressLabel } from '../../shared/components/Forms/parse-google-address.util';
import { isPhoneNumberIncomplete } from '../../shared/components/Forms/PhoneNumberField.component';
import {
  DeliveryPointClosure,
  DeliveryPointDetails,
  DeliveryPointDetailsAddress,
} from '../types/delivery-point-details.type';
import { DeliveryPointSegment, DeliveryPointType } from '../types/delivery-point.type';
import { UpdateDeliveryPointRequestDTO, UpdateDeliveryPointSchedule } from '../types/dtos/update-delivery-point.dto';
import {
  getCompanyCodeError,
  isBoundedTextInvalid,
  isCompanyType,
  isStructuredAddressComplete,
  isZipCodeInvalid,
  toAddressLabel,
} from '../utils/delivery-point-form-rules.util';

type FlatField =
  | 'name'
  | 'description'
  | 'code'
  | 'phone'
  | 'email'
  | 'siret'
  | 'invoicingNumber'
  | 'website'
  | 'isActive';

type FlatValue = string | boolean;

type AddressField = keyof DeliveryPointDetailsAddress;

export type DeliveryPointFieldChange = { field: FlatField; value: FlatValue };

export type DeliveryPointAddressFieldChange = { field: AddressField; value: string };

type EditFormState = {
  updatedDeliveryPoint: DeliveryPointDetails;
  editedFields: UpdateDeliveryPointRequestDTO;
};

type UseDeliveryPointEditFormResult = {
  isEditing: boolean;
  updatedDeliveryPoint: DeliveryPointDetails;
  editedFields: UpdateDeliveryPointRequestDTO;
  addressLabel: string;
  isPlaceSelected: boolean;
  isAddressIncomplete: boolean;
  companyCodeError: string | undefined;
  isFormInvalid: boolean;
  handleFieldChange: (change: DeliveryPointFieldChange) => void;
  handleTypeChange: (type: DeliveryPointType) => void;
  handleAddressFieldChange: (change: DeliveryPointAddressFieldChange) => void;
  handleAddressLabelChange: (value: string) => void;
  handleAddressSelected: (place: ParsedAddress) => void;
  handleSchedulesChange: (schedules: UpdateDeliveryPointSchedule[]) => void;
  handleClosuresChange: (closures: DeliveryPointClosure[]) => void;
  handleSegmentChange: (segmentId: string | null, segment?: DeliveryPointSegment) => void;
  handleDiscountChange: (value: number | null) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: (editedFields: UpdateDeliveryPointRequestDTO) => Promise<boolean>) => Promise<boolean>;
};

const STRUCTURED_ADDRESS_FIELDS: AddressField[] = ['street', 'zipCode', 'city'];

const touchesStructuredAddress = (editedFields: UpdateDeliveryPointRequestDTO): boolean =>
  STRUCTURED_ADDRESS_FIELDS.some((field) => editedFields.address?.[field] !== undefined);

const hasInvalidEditedField = ({ updatedDeliveryPoint, editedFields }: EditFormState): boolean => {
  const { address } = updatedDeliveryPoint;
  const editedAddress = editedFields.address ?? {};

  return (
    (editedFields.phone !== undefined && isPhoneNumberIncomplete(updatedDeliveryPoint.phone ?? '')) ||
    (editedFields.description !== undefined && isBoundedTextInvalid(updatedDeliveryPoint.description ?? '')) ||
    (editedAddress.zipCode !== undefined && isZipCodeInvalid(address.zipCode)) ||
    (editedAddress.city !== undefined && isBoundedTextInvalid(address.city)) ||
    (editedAddress.informations !== undefined && isBoundedTextInvalid(address.informations ?? '')) ||
    (editedAddress.comment !== undefined && isBoundedTextInvalid(address.comment ?? ''))
  );
};

const toCompanyCodeError = ({ updatedDeliveryPoint, editedFields }: EditFormState): string | undefined => {
  const touchesTypeOrCode = editedFields.type !== undefined || editedFields.code !== undefined;
  if (!touchesTypeOrCode || !isCompanyType(updatedDeliveryPoint.type)) return undefined;

  return getCompanyCodeError(updatedDeliveryPoint.code ?? '');
};

export function useDeliveryPointEditForm(deliveryPoint: DeliveryPointDetails): UseDeliveryPointEditFormResult {
  const [form, setForm] = useState<EditFormState>({ updatedDeliveryPoint: deliveryPoint, editedFields: {} });
  const [addressLabel, setAddressLabel] = useState(toAddressLabel(deliveryPoint.address));
  const [isPlaceSelected, setIsPlaceSelected] = useState(true);
  const lastSelectedAddressLabelRef = useRef<string | null>(toAddressLabel(deliveryPoint.address));

  const resetForm = useCallback(() => {
    const initialLabel = toAddressLabel(deliveryPoint.address);

    lastSelectedAddressLabelRef.current = initialLabel;
    setForm({ updatedDeliveryPoint: deliveryPoint, editedFields: {} });
    setAddressLabel(initialLabel);
    setIsPlaceSelected(true);
  }, [deliveryPoint]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const applyEdits = useCallback(
    (deliveryPointEdits: Partial<DeliveryPointDetails>, requestEdits: UpdateDeliveryPointRequestDTO) =>
      setForm((previous) => ({
        updatedDeliveryPoint: { ...previous.updatedDeliveryPoint, ...deliveryPointEdits },
        editedFields: { ...previous.editedFields, ...requestEdits },
      })),
    []
  );

  const updateAddress = useCallback(
    (addressEdits: Partial<DeliveryPointDetailsAddress>) =>
      setForm((previous) => ({
        updatedDeliveryPoint: {
          ...previous.updatedDeliveryPoint,
          address: { ...previous.updatedDeliveryPoint.address, ...addressEdits },
        },
        editedFields: { ...previous.editedFields, address: { ...previous.editedFields.address, ...addressEdits } },
      })),
    []
  );

  const handleFieldChange = useCallback(
    ({ field, value }: DeliveryPointFieldChange) => applyEdits({ [field]: value }, { [field]: value }),
    [applyEdits]
  );

  const handleTypeChange = useCallback(
    (type: DeliveryPointType) =>
      setForm((previous) => {
        if (isCompanyType(type)) {
          return {
            updatedDeliveryPoint: { ...previous.updatedDeliveryPoint, type },
            editedFields: { ...previous.editedFields, type },
          };
        }

        const editedFields: UpdateDeliveryPointRequestDTO = { ...previous.editedFields, type };
        delete editedFields.code;
        delete editedFields.discount;

        return {
          updatedDeliveryPoint: {
            ...previous.updatedDeliveryPoint,
            type,
            code: deliveryPoint.code,
            discount: deliveryPoint.discount,
          },
          editedFields,
        };
      }),
    [deliveryPoint]
  );

  const handleAddressFieldChange = useCallback(
    ({ field, value }: DeliveryPointAddressFieldChange) => updateAddress({ [field]: value }),
    [updateAddress]
  );

  const handleAddressSelected = useCallback(
    (place: ParsedAddress) => {
      lastSelectedAddressLabelRef.current = buildAddressLabel(place);
      setIsPlaceSelected(true);
      updateAddress({ street: place.street, zipCode: place.zipCode, city: place.city, country: place.country });
    },
    [updateAddress]
  );

  const handleAddressLabelChange = useCallback(
    (value: string) => {
      setAddressLabel(value);

      if (value === lastSelectedAddressLabelRef.current) return;

      lastSelectedAddressLabelRef.current = null;
      setIsPlaceSelected(false);
      updateAddress({ street: '', zipCode: '', city: '', country: '' });
    },
    [updateAddress]
  );

  const handleSchedulesChange = useCallback(
    (schedules: UpdateDeliveryPointSchedule[]) => applyEdits({ schedules }, { schedules }),
    [applyEdits]
  );

  const handleClosuresChange = useCallback(
    (closures: DeliveryPointClosure[]) => applyEdits({ closures }, { closures }),
    [applyEdits]
  );

  const handleDiscountChange = useCallback(
    (value: number | null) => {
      const discount = value === null ? undefined : { value, reduction: 'percent' as const };
      applyEdits({ discount }, { discount });
    },
    [applyEdits]
  );

  const handleSegmentChange = useCallback(
    (segmentId: string | null, segment?: DeliveryPointSegment) =>
      applyEdits({ segment: segmentId === null ? undefined : segment }, { segmentId }),
    [applyEdits]
  );

  const handleSave = async (
    updateFn: (editedFields: UpdateDeliveryPointRequestDTO) => Promise<boolean>
  ): Promise<boolean> => {
    const hasDefinedField = Object.values(form.editedFields).some((value) => value !== undefined);
    if (!hasDefinedField) return false;

    return updateFn(form.editedFields);
  };

  const isAddressIncomplete =
    touchesStructuredAddress(form.editedFields) && !isStructuredAddressComplete(form.updatedDeliveryPoint.address);
  const companyCodeError = toCompanyCodeError(form);

  return {
    isEditing: Object.keys(form.editedFields).length > 0,
    updatedDeliveryPoint: form.updatedDeliveryPoint,
    editedFields: form.editedFields,
    addressLabel,
    isPlaceSelected,
    isAddressIncomplete,
    companyCodeError,
    isFormInvalid:
      form.updatedDeliveryPoint.name.length === 0 ||
      hasInvalidEditedField(form) ||
      isAddressIncomplete ||
      companyCodeError !== undefined,
    handleFieldChange,
    handleTypeChange,
    handleAddressFieldChange,
    handleAddressLabelChange,
    handleAddressSelected,
    handleSchedulesChange,
    handleClosuresChange,
    handleSegmentChange,
    handleDiscountChange,
    handleCancelEditing: resetForm,
    handleSave,
  };
}
